import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Box,
  Collapse,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { setSidebarOpen } from '../../store/slices/uiSlice'
import { navigationConfig } from './Sidebar/navigationConfig'
import type { NavGroup } from './Sidebar/types'

const drawerWidth = 240
const collapsedWidth = 64

export const Sidebar: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md')) // < 768px
  const sidebarOpen = useAppSelector((state) => state.ui.sidebarOpen)
  const isRtl = theme.direction === 'rtl'

  // State to track which groups are expanded
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem('sidebar-expanded-groups')
    if (saved) {
      return JSON.parse(saved)
    }
    // Initialize with default expanded states
    const initial: Record<string, boolean> = {}
    navigationConfig.forEach((group) => {
      initial[group.id] = group.defaultExpanded ?? false
    })
    return initial
  })

  // Save expanded groups to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('sidebar-expanded-groups', JSON.stringify(expandedGroups))
  }, [expandedGroups])

  // Auto-expand parent group when route matches
  useEffect(() => {
    const currentPath = location.pathname
    navigationConfig.forEach((group) => {
      const hasActiveItem = group.items.some((item) => item.path === currentPath)
      if (hasActiveItem && !expandedGroups[group.id]) {
        setExpandedGroups((prev) => ({ ...prev, [group.id]: true }))
      }
    })
  }, [location.pathname])

  const handleNavigation = (path: string) => {
    navigate(path)
    // Close drawer on mobile after navigation
    if (isMobile) {
      dispatch(setSidebarOpen(false))
    }
  }

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }))
  }

  const isItemActive = (path: string) => {
    return location.pathname === path
  }

  const handleDrawerClose = () => {
    if (isMobile) {
      dispatch(setSidebarOpen(false))
    }
  }

  const renderNavItem = (item: NavGroup['items'][0], isNested: boolean = false) => {
    const active = isItemActive(item.path)
    const label = t(item.labelKey)

    const listItemButton = (
      <ListItemButton
        onClick={() => handleNavigation(item.path)}
        selected={active}
        sx={{
          minHeight: 48,
          justifyContent: sidebarOpen ? 'initial' : 'center',
          px: 2,
          ...(isRtl ? {
            pr: sidebarOpen && isNested ? 4 : 2,
          } : {
            pl: sidebarOpen && isNested ? 4 : 2,
          }),
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            ...(isRtl ? {
              ml: sidebarOpen ? 2 : 'auto',
              mr: 0,
            } : {
              mr: sidebarOpen ? 2 : 'auto',
              ml: 0,
            }),
            justifyContent: 'center',
            color: active ? 'primary.main' : 'inherit',
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={label}
          sx={{
            opacity: sidebarOpen ? 1 : 0,
            ...(isRtl ? {
              mr: 1,
            } : {
              ml: 0,
            }),
          }}
        />
      </ListItemButton>
    )

    // Wrap in tooltip when sidebar is collapsed (desktop only)
    if (!sidebarOpen && !isMobile) {
      return (
        <Tooltip key={item.id} title={label} placement={isRtl ? 'left' : 'right'} arrow>
          <ListItem disablePadding>
            {listItemButton}
          </ListItem>
        </Tooltip>
      )
    }

    return (
      <ListItem key={item.id} disablePadding>
        {listItemButton}
      </ListItem>
    )
  }

  const renderNavGroup = (group: NavGroup) => {
    const isExpanded = expandedGroups[group.id]
    const hasActiveItem = group.items.some((item) => isItemActive(item.path))
    const groupLabel = t(group.labelKey)

    const groupButton = (
      <ListItemButton
        onClick={() => toggleGroup(group.id)}
        sx={{
          minHeight: 48,
          justifyContent: sidebarOpen ? 'initial' : 'center',
          px: 2,
          backgroundColor: hasActiveItem ? 'action.selected' : 'transparent',
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            ...(isRtl ? {
              ml: sidebarOpen ? 2 : 'auto',
              mr: 0,
            } : {
              mr: sidebarOpen ? 2 : 'auto',
              ml: 0,
            }),
            justifyContent: 'center',
            color: hasActiveItem ? 'primary.main' : 'inherit',
          }}
        >
          {group.icon}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              variant="body2"
              sx={{
                fontWeight: hasActiveItem ? 600 : 400,
                opacity: sidebarOpen ? 1 : 0,
                ...(isRtl ? {
                  mr: 1,
                } : {
                  ml: 0,
                }),
              }}
            >
              {groupLabel}
            </Typography>
          }
        />
        {sidebarOpen && (
          <Box sx={{ ...(isRtl ? { mr: 'auto', ml: 1 } : { ml: 'auto', mr: 1 }) }}>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </Box>
        )}
      </ListItemButton>
    )

    return (
      <React.Fragment key={group.id}>
        {!sidebarOpen && !isMobile ? (
          <Tooltip title={groupLabel} placement={isRtl ? 'left' : 'right'} arrow>
            <ListItem disablePadding>
              {groupButton}
            </ListItem>
          </Tooltip>
        ) : (
          <ListItem disablePadding>
            {groupButton}
          </ListItem>
        )}

        {sidebarOpen && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {group.items.map((item) => renderNavItem(item, true))}
            </List>
          </Collapse>
        )}

        {/* When collapsed, show items on hover/click (desktop only) */}
        {!sidebarOpen && !isMobile && isExpanded && (
          <List component="div" disablePadding>
            {group.items.map((item) => renderNavItem(item, false))}
          </List>
        )}
      </React.Fragment>
    )
  }

  const drawerContent = (
    <>
      <Toolbar />
      <Divider />
      <Box
        id="navigation"
        component="nav"
        aria-label={t('common.mainNavigation')}
        sx={{ overflow: 'auto' }}
      >
        <List>
          {navigationConfig.map((group) => renderNavGroup(group))}
        </List>
      </Box>
    </>
  )

  return (
    <>
      {/* Mobile drawer (temporary) */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor={isRtl ? 'right' : 'left'}
          open={sidebarOpen}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              ...(isRtl ? {
                right: 0,
                left: 'auto',
                borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                borderRight: 'none',
              } : {
                left: 0,
                right: 'auto',
                borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                borderLeft: 'none',
              }),
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        /* Desktop drawer (permanent) */
        <Drawer
          variant="permanent"
          anchor={isRtl ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', md: 'block' },
            width: sidebarOpen ? drawerWidth : collapsedWidth,
            flexShrink: 0,
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            '& .MuiDrawer-paper': {
              width: sidebarOpen ? drawerWidth : collapsedWidth,
              boxSizing: 'border-box',
              transition: (theme) =>
                theme.transitions.create('width', {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              overflowX: 'hidden',
              ...(isRtl ? {
                right: 0,
                left: 'auto',
                borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                borderRight: 'none',
              } : {
                left: 0,
                right: 'auto',
                borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                borderLeft: 'none',
              }),
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  )
}